import React, { useState, useEffect } from 'react';
import bcrypt from 'bcryptjs';
import { ref, uploadBytes, deleteObject, getDownloadURL, listAll } from 'firebase/storage';
import { storage } from '../firebase';
import imageCompression from 'browser-image-compression';
import './admin.css';

const Admin = () => {
  // State for authentication
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [imageURL, setImageURL] = useState('');

  const [files, setFiles] = useState([]);
  const [fileNames, setFileNames] = useState([]);
  const [imageURLs, setImageURLs] = useState([]);

  const [members, setMembers] = useState([]);
  const [previewURL, setPreviewURL] = useState('');

  const FIREBASE_FUNCTION_URL = 'us-central1-gsp-website-553a2.cloudfunctions.net';

  // State for managing events
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    registrationLink: '',
    description: ''
  });

  const [newMember, setNewMember] = useState({
    name: '',
    designation: '',
    photoURL: ''
  });

  const handleImageSelect = (url) => {
    if (selectedImages.includes(url)) {
      // Deselect image
      setSelectedImages(selectedImages.filter((imageUrl) => imageUrl !== url));
    } else {
      // Select image
      setSelectedImages([...selectedImages, url]);
    }
  };

  const handleMemberImageFileChange = (e) => {
    const selectedFile = e.target.files[0]; // Get the single selected file
    setFile(selectedFile);
    setFileName(selectedFile.name); // Set file name

    // Create preview URL for the selected file
    const preview = URL.createObjectURL(selectedFile);
    setPreviewURL(preview); // Set preview URL
  };

  const handleImageFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files); // Get all selected files
    setFiles(selectedFiles);
    setFileNames(selectedFiles.map((file) => file.name)); // Extract file names

    const previewURLs = selectedFiles.map((file) =>
      URL.createObjectURL(file)
    );
    setImageURLs(previewURLs); // Create preview URLs for all files
  };


  const handleMemberImageUpload = async () => {
    if (!newMember.photoURL) {
      alert("Member photo not selected");
      return false;
    }

    try {
      let fileToUpload = newMember.photoURL;

      if (fileToUpload.size > 500 * 1024) {
        const options = {
          maxSizeMB: 1.5,
          maxWidthOrHeight: 1024 * 1.5,
          useWebWorker: true,
        };
        fileToUpload = await imageCompression(fileToUpload, options);
      }

      const storageRef = ref(storage, `Members/${fileToUpload.name}`);
      await uploadBytes(storageRef, fileToUpload);
      const url = await getDownloadURL(storageRef);
      newMember.photoURL = url;

      setNewMember((prevState) => ({
        ...prevState,
        photoURL: url, // Set the uploaded image URL after uploading
      }));

      //alert("Image uploaded successfully!");
      return true;
    } catch (error) {
      console.error('Error uploading image:', error);
      return false;
    }
  };

  const handleImageUpload = async () => {
    if (files.length === 0) {
      alert('Please select some files to upload.');
      return;
    }

    const confirmation = window.confirm(`Are you sure you want to upload ${files.length} image(s)?`);
    if(confirmation){
      try {
        files.forEach(async (file) => {
          try {
            let fileToUpload = file;

            // Check if the file size exceeds 500KB (500 * 1024 bytes)
            if (file.size > 500 * 1024) {
              // Compress the image if it's larger than 500KB
              const options = {
                maxSizeMB: 1, // Maximum size after compression
                maxWidthOrHeight: 1024, // Maximum width or height
                useWebWorker: true, // Optional, use web worker for better performance
              };
              fileToUpload = await imageCompression(file, options);
              //console.log(`File compressed from ${file.size / 1024}KB to ${fileToUpload.size / 1024}KB`);
            }

            // Create a Firebase storage reference
            const storageRef = ref(storage, `Gallery/${fileToUpload.name}`);

            // Upload the compressed or original image
            await uploadBytes(storageRef, fileToUpload);

            // Get the download URL
            const url = await getDownloadURL(storageRef);
            setImageURL(url);

          } catch (error) {
            //console.error('Error uploading image:', error);
          }
          //console.log(`Uploading ${file.name}`);
        });
        alert('Images uploaded successfully!');
      } catch (error) {
        //console.error('Error uploading images:', error);
      }
    }
  };

  const deleteImageFromGallery = async (imageUrl) => {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      //console.error('Error deleting image:', error);
    }
  };

  const deleteMemberPhoto = async (imageUrl) => {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      //console.log(`Deleted image: ${imageUrl}`);
    } catch (error) {
      //console.error('Error deleting image:', error);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedImages.length === 0) {
      alert('No images selected for deletion.');
      return;
    }

    const confirmation = window.confirm(`Are you sure you want to delete ${selectedImages.length} image(s)?`);
    if (confirmation) {
      selectedImages.forEach((imageUrl) => {
        deleteImageFromGallery(imageUrl);
      });

      // Update the images list after deletion
      const updatedImages = images.filter((imageUrl) => !selectedImages.includes(imageUrl));
      setImages(updatedImages);
      setSelectedImages([]);
    }
  };

  const fetchImages = async () => {
    try {
      // Create a reference to the 'images' directory
      const storageRef = ref(storage, 'Gallery');
      // List all items (files) in the 'images' directory
      const imageRefs = await listAll(storageRef);

      // Get URLs for all image files
      const urls = await Promise.all(
        imageRefs.items.map(async (itemRef) => {
          try {
            const url = await getDownloadURL(itemRef);
            return url;
          } catch (error) {
            //console.error('Error getting download URL for', itemRef.name, error);
            return null;
          }
        })
      );

      setImages(urls.filter(url => url !== null));
    } catch (error) {
      //console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle login
  const handleLogin = async () => {
    try {
      // Fetch credentials.json from Firebase Cloud
      const response = await fetch(`https://${FIREBASE_FUNCTION_URL}/getCredential`);
      const data = await response.json();

      // Check if username matches
      if (username !== data.username) {
        setAuthError('Invalid username');
        return;
      }

      // Compare password with hashed password
      const validPassword = await bcrypt.compare(password, data.passwordHash);
      if (validPassword) {
        setAuthenticated(true); // Set authentication to true
        setAuthError('');
      } else {
        setAuthError('Invalid password');
      }
    } catch (error) {
      //console.error('Error during authentication:', error);
      setAuthError('Authentication failed');
    }
  };

  // Fetch events after authentication
  useEffect(() => {
    if (authenticated) {
      fetch(`https://${FIREBASE_FUNCTION_URL}/getEvents`)
        .then(response => response.json())
        .then(data => {
          setEvents(data);
          setLoading(false);
        })
        .catch(error => {
          //console.error('Error fetching events:', error);
          setLoading(false);
        });
    }
    fetchImages();
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) {
      fetch(`https://${FIREBASE_FUNCTION_URL}/getMembers`)
        .then(response => response.json())
        .then(data => {
          setMembers(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching events:', error);
          setLoading(false);
        });
    }
  }, [authenticated]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent({ ...newEvent, [name]: value });
  };

  const handleMemberInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember({ ...newMember, [name]: value });
  };

  const validateEvent = () => {
    const { title, date, registrationLink, description } = newEvent;
    if (!title || !date || !registrationLink || !description) {
      setError('All fields are required');
      return false;
    }
    setError('');
    return true;
  };

  const validateMember = () => {
    const { name, designation, photoURL } = newMember;
    if (!name || !designation || !photoURL) {
      setError('All fields are required');
      return false;
    }
    setError('');
    return true;
  };

  const handleDeleteMember = (id) => {
    const confirmation = window.confirm(`Are you sure you want to delete this member?`);
    if(confirmation){
      fetch(`https://${FIREBASE_FUNCTION_URL}/deleteMember`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      })
      .then(response => {
        if (response.ok) {
          setMembers(prevMembers => prevMembers.filter(member => member.id !== id));
          alert('Member deleted successfully');
        } else {
          alert('Error deleting member');
        }
      })
      .catch(error => {
        //console.error('Error:', error);
        alert('Error deleting member');
      });
    }
  };

  const handleAddEvent = () => {
    if (validateEvent()) {
      fetch(`https://${FIREBASE_FUNCTION_URL}/addEvent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newEvent),
      })
        .then((response) => response.text())
        .then((data) => {
          //console.log('Success:', data);
          setEvents([...events, { ...newEvent, id: new Date().getTime() }]); // Temporarily generate ID
          setNewEvent({
            title: '',
            date: '',
            registrationLink: '',
            description: '',
          });
        })
        .catch((error) => {
          //console.error('Error:', error);
        });
    }
  };

  const handleDelete = (id) => {
    const confirmation = window.confirm(`Are you sure you want to delete this event?`);
    if(confirmation){
      fetch(`https://${FIREBASE_FUNCTION_URL}/deleteEvent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      })
      .then(response => {
        if (response.ok) {
          setEvents(prevEvents => prevEvents.filter(event => event.id !== id));
          alert('Event deleted successfully');
        } else {
          alert('Error deleting event');
        }
      })
      .catch(error => {
        alert('Error deleting event');
      });
    }
  };

  const handleUpdateMember = async (id, updatedMember) => {
    let photoURL = updatedMember.photoURL; // Existing photoURL

    // Check if the member photo is a file and upload if necessary
    if (updatedMember.photoURL instanceof File) {
      const file = updatedMember.photoURL;

      try {
        let fileToUpload = file;

        // Compress the image if it's larger than 500KB
        if (file.size > 500 * 1024) {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
          };
          fileToUpload = await imageCompression(file, options);
        }

        // Create a Firebase storage reference
        const storageRef = ref(storage, `Members/${fileToUpload.name}`);

        // Upload the compressed or original image
        await uploadBytes(storageRef, fileToUpload);

        // Get the download URL and update the member object
        photoURL = await getDownloadURL(storageRef);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Error uploading the photo. Please try again.');
        return;
      }
    } else {
      console.log("No photo");
    }

    // Proceed to update the member details after image upload (or if no new image is uploaded)
    try {
      fetch(`https://${FIREBASE_FUNCTION_URL}/updateMember`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...updatedMember, photoURL }), // Ensure correct photoURL
      })
        .then((response) => {
          if (response.ok) {
            setMembers(members.map(member => (member.id === id ? { ...updatedMember, photoURL } : member)));
            alert('Member updated successfully');
          } else {
            alert('Error updating member');
          }
        })
        .catch((error) => {
          console.error('Error updating member:', error);
          alert('Error updating member');
        });
    } catch (error) {
      console.error('Error updating member:', error);
      alert('Error updating member');
    }
  };

  const handleNewMemberImageFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setNewMember((prevState) => ({
      ...prevState,
      photoURL: selectedFile, // Store the selected file in the state
    }));
  };

  const handleNewMemberInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAddMember = async () => {
    if (validateMember()) {
      const uploadSuccess = await handleMemberImageUpload(); // Wait for the image upload
      if (!uploadSuccess) return;

      // Add the member after image upload
      fetch(`https://${FIREBASE_FUNCTION_URL}/addMember`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newMember),
      })
        .then((response) => response.text())
        .then((data) => {
          const maxId = members.length > 0 ? Math.max(...members.map(member => member.id)) : 0;
          const newId = maxId + 1;
          setMembers([...members, { ...newMember, id: newId }]);
          setNewMember({
            name: '',
            designation: '',
            photoURL: ''
          });
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }
  };

  const handleUpdate = (id, updatedEvent) => {
    fetch(`https://${FIREBASE_FUNCTION_URL}/updateEvents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedEvent),
    })
      .then((response) => {
        if (response.ok) {
          setEvents(events.map(event => (event.id === id ? updatedEvent : event)));
          alert('Event updated successfully');
        } else {
          alert('Error updating event');
        }
      })
      .catch((error) => {
        //console.error('Error:', error);
        alert('Error updating event');
      });
  };

  const handleEventChange = (id, field, value) => {
    setEvents((prevEvents) =>
      prevEvents.map((event) =>
        event.id === id ? { ...event, [field]: value } : event
      )
    );
  };

  const handleMemberChange = (id, field, value) => {
    setMembers((prevMembers) =>
      prevMembers.map((member) =>
        member.id === id
          ? { ...member, [field]: field === 'photoURL' ? value.target.files[0] : value } // Separate file logic
          : member
      )
    );
  };

  if (!authenticated) {
    // Show login form if not authenticated
    return (
      <div className="login-container">
        <h2>ADMIN LOGIN</h2>
        {authError && <p className="error-message">{authError}</p>}
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button onClick={handleLogin}>LOGIN</button>
      </div>

    );
  }

  // Render admin panel after authentication
  return (
    <div className="admin-container">
      <h1>ADMIN</h1>
      <h1>MANAGE EVENTS</h1>

      {loading ? (
        <p>Loading events...</p>
      ) : (
        <div>
          <h2>Existing Events</h2>
          <ul>
            {events.map(event => (
              <li key={event.id} className="event-item-admin">
                <p>Name</p>
                <input
                  type="text"
                  value={event.title}
                  onChange={(e) => handleEventChange(event.id, 'title', e.target.value)}
                  placeholder="Title"
                />
                <p>Date</p>
                <input
                  type="date"
                  value={event.date}
                  onChange={(e) => handleEventChange(event.id, 'date', e.target.value)}
                />
                <p>Registration Link</p>
                <input
                  type="text"
                  value={event.registrationLink}
                  onChange={(e) => handleEventChange(event.id, 'registrationLink', e.target.value)}
                  placeholder="Registration Link"
                />
                <p>Event Description</p>
                <textarea
                  value={event.description}
                  onChange={(e) => handleEventChange(event.id, 'description', e.target.value)}
                  placeholder="Description"
                />
                <button onClick={() => handleUpdate(event.id, event)}>Update</button>
                <button onClick={() => handleDelete(event.id)}>Delete</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2>Add New Event</h2>
      <div className="add-event-container">
        {error && <p className="error-message">{error}</p>}
        <input
          type="text"
          name="title"
          value={newEvent.title}
          onChange={handleInputChange}
          placeholder="Title"
        />
        <input
          type="date"
          name="date"
          value={newEvent.date}
          onChange={handleInputChange}
        />
        <input
          type="text"
          name="registrationLink"
          value={newEvent.registrationLink}
          onChange={handleInputChange}
          placeholder="Registration Link"
        />
        <textarea
          name="description"
          value={newEvent.description}
          onChange={handleInputChange}
          placeholder="Description"
        />
        <button onClick={handleAddEvent}>Add Event</button>
      </div>

      <hr />

      <h1>MANAGE MEMBERS</h1>

      {loading ? (
        <p>Loading member info...</p>
      ) : (
        <div>
          <h2>Existing Members</h2>
          <ul>
            {members.map(member => (
              <li key={member.id} className="event-item-admin">
                <p>Name</p>
                <input
                  type="text"
                  value={member.name}
                  onChange={(f) => handleMemberChange(member.id, 'name', f.target.value)}
                  placeholder="Name"
                />
                <p>Designation</p>
                <input
                  type="text"
                  value={member.designation}
                  onChange={(f) => handleMemberChange(member.id, 'designation', f.target.value)}
                  placeholder="Designation"
                />
                <p>Upload new photo</p>
                <input
                  type="file"
                  onChange={(f) => handleMemberChange(member.id, 'photoURL', f)}
                  placeholder="Profile Photo"
                />
                <button onClick={() => handleUpdateMember(member.id, member)}>Update</button>
                <button onClick={() => handleDeleteMember(member.id)}>Delete</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <h2>Add New Member</h2>
      <div className="add-event-container">
        {error && <p className="error-message">{error}</p>}
        <input
          type="text"
          name="name"
          value={newMember.name}
          onChange={handleNewMemberInputChange}
          placeholder="Name"
        />
        <input
          type="text"
          name="designation"
          value={newMember.designation}
          onChange={handleNewMemberInputChange}
          placeholder="Designation"
        />
        <input
          type="file"
          name="photoURL"
          onChange={handleNewMemberImageFileChange} // Use a separate handler for file inputs
          accept="image/*"
        />
        <button onClick={handleAddMember}>Add Member</button>
      </div>

      <hr />

      <h1>MANAGE GALLERY</h1>
      <div className="upload-images">
        <h2>Upload Images to Gallery</h2>
        <label className="custom-file-upload">
          <input type="file" multiple onChange={handleImageFileChange} />
          Choose Files
        </label>
        <button onClick={handleImageUpload}>Upload Images</button>

        {/* Display selected file names */}
        {fileNames.length > 0 && (
          <div className="file-names">
            <h3>Selected Files:</h3>
            <ul>
              {fileNames.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="delete-images">
        <h2>Delete Images from Gallery</h2>
        {loading ? (
          <p>Loading images...</p>
        ) : (
          <div>
            {images.length > 0 ? (
              <div className="gallery-grid">
                {images.map((url, index) => (
                  <div key={index} className="gallery-item">
                    <input
                      type="checkbox"
                      checked={selectedImages.includes(url)}
                      onChange={() => handleImageSelect(url)}
                    />
                    <img src={url} alt={`Gallery Item ${index + 1}`} />
                  </div>
                ))}
              </div>
            ) : (
              <p>No images found.</p>
            )}
          </div>
        )}

        {selectedImages.length > 0 && (
          <div className="delete-actions">
            <button onClick={handleDeleteSelected}>Delete Selected</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
